import React, { useCallback, useMemo } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  IconButton,
} from '@mui/material'
import tw from 'twin.macro'
import { TeamRole } from 'src/interfaces/team'
import { CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { groupPermissionsByCategory } from '../utils'
import { removeUnderscoresAndCapitalize } from 'src/utils/baseUtils'
import { useSelector } from 'react-redux'
import { getTeamPermissions } from 'src/selectors/team'

interface Props {
  teamRole: TeamRole
  selected: boolean
  expanded: boolean
  onRoleClick: (role: TeamRole) => void
  onAccordionChange: (roleId: string) => void
}

export const TeamRoleItem = ({
  teamRole,
  selected,
  expanded,
  onRoleClick,
  onAccordionChange,
}: Props) => {
  const teamPermissions = useSelector(getTeamPermissions)

  const mappedPermissions = useMemo(
    () =>
      teamPermissions ? groupPermissionsByCategory(teamPermissions, teamRole.permissions) : {},
    [teamPermissions, teamRole.permissions],
  )
  const permissionsCount = useMemo(
    () =>
      Object.values(mappedPermissions).reduce(
        (acc, perms) => acc + ((perms && perms.length) || 0),
        0,
      ),
    [mappedPermissions],
  )

  const handleAccordionChange = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      onAccordionChange(teamRole.id)
    },
    [onAccordionChange, teamRole.id],
  )

  return (
    <Accordion
      expanded={expanded}
      css={tw`shadow-none border-none`}
      disableGutters
      sx={{
        '&::before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        css={tw`flex items-center w-full justify-between px-4 transition-colors duration-300 hover:bg-[var(--Shades-Grey-25,#FBFBFB)]`}
        onClick={() => onRoleClick(teamRole)}
        sx={{
          position: 'relative',
          '&::before': { display: 'none' },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '3rem',
            right: '1rem',
            bottom: 0,
            borderBottom: '1px solid #EDEDED',
            pointerEvents: 'none',
          },
        }}
      >
        <div css={tw`flex items-center justify-center h-10`}>
          <Checkbox
            checked={selected}
            icon={<RadioButtonUncheckedIcon sx={{ fontSize: 20, color: colors.SHADES_GREY_200 }} />}
            checkedIcon={<CheckCircleIcon sx={{ fontSize: 20, color: colors.COLORS_GREEN_500 }} />}
            sx={{
              padding: 0,
              '& .MuiTouchRipple-root': {
                borderRadius: '50%',
                overflow: 'hidden',
              },
            }}
          />
        </div>
        <div css={tw`flex-1 ml-3`}>
          <Typography variant="h6" css={[selected && tw`text-[#27AE60]`]}>
            {removeUnderscoresAndCapitalize(teamRole.name)}
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_400}>
            {permissionsCount} permissions
          </Typography>
        </div>
        <div css={tw`flex items-center justify-center h-10`}>
          <IconButton
            onClick={handleAccordionChange}
            size="small"
            css={[
              tw`w-8 h-8 flex items-center justify-center border border-solid border-[#EDEDED] rounded-lg`,
              expanded && tw`bg-[#EDEDED]`,
            ]}
          >
            {expanded ? <CaretUpMinor width={20} /> : <CaretDownMinor width={20} />}
          </IconButton>
        </div>
      </AccordionSummary>
      <AccordionDetails
        css={tw`flex flex-col px-0 py-3.5 mx-4 gap-2 border-0 border-b border-[#EDEDED] border-solid`}
      >
        {Object.entries(mappedPermissions).map(([category, permissions]) => (
          <div key={category}>
            <Typography variant="body2" css={tw`font-medium leading-5`}>
              {category}
            </Typography>
            <Typography variant="body2" color={colors.SHADES_GREY_400}>
              {(permissions || []).join(', ')}
            </Typography>
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}
